import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet';
import {
    Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from './ui/accordion';
import { Button } from './ui/button';
import { getModuleKeyFromPath, getHelpForModule } from '../data/helpContent';
import { useAuth } from '../contexts/AuthContext';

/**
 * Botón de ayuda flotante que aparece en la esquina inferior derecha
 * de cada módulo. Detecta la ruta actual automáticamente y muestra
 * el contenido de ayuda correspondiente en un panel lateral (Sheet).
 *
 * Uso: se inyecta en DashboardLayout y AdminLayout — no requiere pasar props.
 * Si la ruta no tiene contenido de ayuda registrado, el botón no se renderiza.
 */
export const HelpButton = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    // Auth de aerolínea (opcional; en admin no hay user de aerolínea)
    let userRole = 'default';
    try {
        // useAuth puede fallar si el provider no envuelve — lo capturamos
        const auth = useAuth();
        if (auth?.user?.role) userRole = auth.user.role;
    } catch (e) {
        userRole = 'default';
    }

    const moduleKey = getModuleKeyFromPath(location.pathname);
    if (!moduleKey) return null;

    const content = getHelpForModule(moduleKey, userRole);
    if (!content) return null;

    return (
        <>
            <Button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="help-button"
                aria-label="Abrir ayuda del módulo"
                className="fixed bottom-6 right-6 z-40 h-12 w-12 rounded-full p-0 shadow-lg bg-amber-500 hover:bg-amber-600 text-black transition-transform hover:scale-110"
            >
                <HelpCircle className="w-6 h-6" />
            </Button>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent
                    side="right"
                    className="w-full sm:max-w-md overflow-y-auto"
                    data-testid="help-panel"
                >
                    <SheetHeader className="text-left">
                        <SheetTitle
                            className="text-2xl font-bold uppercase tracking-tight font-['Barlow_Condensed']"
                            data-testid="help-panel-title"
                        >
                            Ayuda · {content.title}
                        </SheetTitle>
                        <SheetDescription>
                            Instrucciones rápidas para este módulo.
                        </SheetDescription>
                    </SheetHeader>

                    <Accordion
                        type="single"
                        collapsible
                        defaultValue="purpose"
                        className="mt-6"
                    >
                        <AccordionItem value="purpose" data-testid="help-section-purpose">
                            <AccordionTrigger className="text-sm font-semibold">
                                Qué hace este módulo
                            </AccordionTrigger>
                            <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                                {content.purpose}
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="steps" data-testid="help-section-steps">
                            <AccordionTrigger className="text-sm font-semibold">
                                Cómo usarlo paso a paso
                            </AccordionTrigger>
                            <AccordionContent>
                                <ol className="list-decimal pl-5 space-y-2 text-sm text-muted-foreground leading-relaxed">
                                    {content.steps.map((step, i) => (
                                        <li key={i}>{step}</li>
                                    ))}
                                </ol>
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="tips" data-testid="help-section-tips">
                            <AccordionTrigger className="text-sm font-semibold">
                                Errores comunes a evitar
                            </AccordionTrigger>
                            <AccordionContent>
                                <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground leading-relaxed">
                                    {content.tips.map((tip, i) => (
                                        <li key={i}>{tip}</li>
                                    ))}
                                </ul>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </SheetContent>
            </Sheet>
        </>
    );
};

export default HelpButton;
